import authService from '../services/authService';

/**
 * @typedef {Object} GraphQLResponse
 * @property {Object} data - Response data
 * @property {Array} [errors] - GraphQL errors
 */

/**
 * @typedef {Object} RequestOptions
 * @property {Object} headers - Request headers
 * @property {boolean} skipAuth - Skip authentication headers
 * @property {number} timeout - Request timeout in milliseconds
 */

class GraphQLClient {
  static CONFIG = Object.freeze({
    DEFAULT_TIMEOUT: 10000,
  });

  static ERROR_TYPES = Object.freeze({
    TOKEN_EXPIRED: 'TOKEN_EXPIRED',
    NETWORK_ERROR: 'NETWORK_ERROR',
    GRAPHQL_ERROR: 'GRAPHQL_ERROR',
    TIMEOUT_ERROR: 'TIMEOUT_ERROR',
    PERMISSION_ERROR: 'PERMISSION_ERROR',
  });

  constructor(endpoint, defaultHeaders = {}) {
    this.endpoint = endpoint;
    this.defaultHeaders = Object.freeze({
      'Content-Type': 'application/json',
      ...defaultHeaders,
    });

    this.isRefreshing = false;
    this.refreshPromise = null;
  }

  /**
   * Get authorization headers with current token
   * @private
   * @returns {Object} Authorization headers
   */
  _getAuthHeaders() {
    const accessToken = authService.getAccessToken();
    return accessToken ? { Authorization: `Bearer ${accessToken}` } : {};
  }

  /**
   * Build request headers
   * @private
   * @param {RequestOptions} options - Request options
   * @returns {Object} Complete headers object
   */
  _buildHeaders(options = {}) {
    const headers = { ...this.defaultHeaders };

    if (!options.skipAuth) {
      Object.assign(headers, this._getAuthHeaders());
    }

    return headers;
  }

  /**
   * Handle GraphQL-specific errors
   * @private
   * @param {Array} errors - GraphQL errors array
   * @throws {Error} Mapped error with appropriate type
   */
  _handleGraphQLErrors(errors) {
    if (!errors || errors.length === 0) return;

    const firstError = errors[0];
    const message = firstError?.message || 'GraphQL error occurred';
    const extensions = firstError?.extensions || {};
    const errorCode = extensions.code;

    if (this._isAuthenticationError(errorCode, message)) {
      const error = new Error('Authentication token expired');
      error.type = GraphQLClient.ERROR_TYPES.TOKEN_EXPIRED;
      throw error;
    }

    const friendlyMessage = this._translateErrorMessage(message);
    const error = new Error(friendlyMessage);
    error.type = GraphQLClient.ERROR_TYPES.GRAPHQL_ERROR;
    error.originalMessage = message;
    error.extensions = extensions;

    throw error;
  }

  /**
   * Check if error is authentication-related
   * @private
   * @param {string} errorCode - Error code
   * @param {string} message - Error message
   * @returns {boolean} Whether error is auth-related
   */
  _isAuthenticationError(errorCode, message) {
    const authErrorCodes = ['invalid-jwt', 'access-denied', 'unauthenticated', 'jwt-expired', 'jwtexpired'];
    const authErrorMessages = [
      'jwtexpired',
      'jwt expired',
      'jwt-expired',
      'could not verify',
      'token',
      'unauthorized',
      'expired',
    ];

    const lowerMessage = message?.toLowerCase() || '';
    const lowerCode = errorCode?.toLowerCase() || '';

    return (
      authErrorCodes.includes(lowerCode) ||
      authErrorMessages.some(keyword => lowerMessage.includes(keyword))
    );
  }

  /**
   * Translate technical error messages to user-friendly ones
   * @private
   * @param {string} message - Technical error message
   * @returns {string} User-friendly error message
   */
  _translateErrorMessage(message) {
    const errorMap = {
      'invalid email address or password': 'E-posta veya şifre hatalı.',
      'invalid credentials': 'E-posta veya şifre hatalı.',
      'invalid email address': 'Geçersiz e-posta adresi.',
      'current password is incorrect': 'Mevcut şifre hatalı.',
      'password is incorrect': 'Şifre hatalı.',
      'user not found': 'Kullanıcı bulunamadı.',
      'account is disabled': 'Hesap devre dışı bırakılmış.',
      'account is locked': 'Hesap kilitlenmiş.',
      'validation-failed': 'Girilen veriler geçersiz. Lütfen kontrol edin.',
      'constraint-error':
        'Bu işlem veri kısıtlaması nedeniyle gerçekleştirilemiyor.',
      'duplicate entry': 'Bu bilgi zaten mevcut.',
      'required field': 'Zorunlu alan eksik.',
      'network-error':
        'Ağ bağlantı hatası. İnternet bağlantınızı kontrol edin.',
      timeout: 'İstek zaman aşımına uğradı. Lütfen tekrar deneyin.',
      'server-error': 'Sunucu hatası. Lütfen daha sonra tekrar deneyin.',
      'insufficient permissions': 'Bu işlem için yetkiniz bulunmuyor.',
      'access denied': 'Erişim reddedildi.',
      forbidden: 'Bu işlemi gerçekleştirme yetkiniz yok.',
    };

    const lowerMessage = message.toLowerCase();

    if (errorMap[lowerMessage]) {
      return errorMap[lowerMessage];
    }

    for (const [key, translation] of Object.entries(errorMap)) {
      if (lowerMessage.includes(key)) {
        return translation;
      }
    }

    return message || 'Beklenmeyen bir hata oluştu';
  }

  /**
   * Handle HTTP response errors
   * @private
   * @param {Response} response - Fetch response object
   * @throws {Error} Mapped HTTP error
   */
  async _handleHttpError(response) {
    let errorMessage = `HTTP ${response.status}`;
    let errorType = GraphQLClient.ERROR_TYPES.NETWORK_ERROR;

    try {
      const errorText = await response.text();
      errorMessage += `: ${errorText}`;
    } catch (e) {
      // Ignore parsing errors
    }

    if (response.status === 401) {
      errorType = GraphQLClient.ERROR_TYPES.TOKEN_EXPIRED;
      errorMessage = 'Authentication required';
    } else if (response.status === 403) {
      errorType = GraphQLClient.ERROR_TYPES.PERMISSION_ERROR;
      errorMessage = 'Access forbidden';
    } else if (response.status >= 500) {
      errorMessage = 'Server error occurred';
    }

    const error = new Error(errorMessage);
    error.type = errorType;
    error.status = response.status;
    throw error;
  }

  /**
   * Attempt token refresh with proper state management
   * @private
   * @returns {Promise<boolean>} Success status
   */
  async _attemptTokenRefresh() {
    if (this.isRefreshing) {
      if (this.refreshPromise) {
        return await this.refreshPromise;
      }
      return false;
    }

    this.isRefreshing = true;

    this.refreshPromise = (async () => {
      try {
        const result = await authService.refreshTokens();

        if (result.success) {
          return true;
        } else {
          return false;
        }
      } catch (error) {
        return false;
      } finally {
        this.isRefreshing = false;
        this.refreshPromise = null;
      }
    })();

    return await this.refreshPromise;
  }

  /**
   * Execute GraphQL request with comprehensive error handling
   * @private
   * @param {string} query - GraphQL query/mutation
   * @param {Object} variables - Query variables
   * @param {RequestOptions} options - Request options
   * @param {boolean} isRetry - Whether this is a retry attempt
   * @returns {Promise<Object>} GraphQL response data
   */
  async _executeRequest(query, variables = {}, options = {}, isRetry = false) {
    try {
      const headers = this._buildHeaders(options);

      const controller = new AbortController();
      const timeoutId = setTimeout(
        () => controller.abort(),
        options.timeout || GraphQLClient.CONFIG.DEFAULT_TIMEOUT
      );

      const response = await fetch(this.endpoint, {
        method: 'POST',
        headers,
        body: JSON.stringify({ query, variables }),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        await this._handleHttpError(response);
      }

      const result = await response.json();

      if (result.errors) {
        this._handleGraphQLErrors(result.errors);
      }

      return result.data;
    } catch (error) {
      if (error.type === GraphQLClient.ERROR_TYPES.TOKEN_EXPIRED && !isRetry) {
        const refreshSuccess = await this._attemptTokenRefresh();

        if (refreshSuccess) {
          return await this._executeRequest(query, variables, options, true);
        } else {
          this._handleAuthFailure();
          throw new Error('Oturum süresi dolmuş. Lütfen tekrar giriş yapın.');
        }
      }

      throw error;
    }
  }

  
  /**
   * Handle authentication failure
   * @private
   */
  _handleAuthFailure() {
    // Clear auth data and redirect to login
    authService._clearAuthData();

    // Dispatch custom event for app-wide handling
    window.dispatchEvent(new CustomEvent('auth:session-expired'));

    // Redirect to login if in browser environment
    if (typeof window !== 'undefined' && window.location) {
      setTimeout(() => {
        window.location.href = '/login';
      }, 100);
    }
  }

  /**
   * Execute GraphQL query
   * @param {string} query - GraphQL query string
   * @param {Object} variables - Query variables
   * @param {RequestOptions} options - Request options
   * @returns {Promise<Object>} Query result
   */
  async query(query, variables = {}, options = {}) {
    return await this._executeRequest(query, variables, options);
  }

  /**
   * Execute GraphQL mutation
   * @param {string} mutation - GraphQL mutation string
   * @param {Object} variables - Mutation variables
   * @param {RequestOptions} options - Request options
   * @returns {Promise<Object>} Mutation result
   */
  async mutate(mutation, variables = {}, options = {}) {
    return await this._executeRequest(mutation, variables, options);
  }
}

// Configuration
const HASURA_CONFIG = Object.freeze({
  endpoint: process.env.REACT_APP_HASURA_ENDPOINT,
});

// Create singleton client instance
// JWT token will be added automatically via _getAuthHeaders() method
export const hasuraClient = new GraphQLClient(HASURA_CONFIG.endpoint);

export default hasuraClient;
